/* Base translations · Digital audit page and its tabs (correlation, inventory, ratios, AI readout) — moved unchanged out of lib/i18n.js (STEP 2 · B2).
   Top-level keys owned by this file (no other base file and not i18n.js define them): daPage, correlationTab, inventoryTab, ratios, aiReadout. */
export const I18N_BASE_DIGITAL_AUDIT_TABS = {
  en: {
    daPage: {
      title: "Digital Audit",
      desc: "Full-population correlation and sub-ledger analytics (Sales/Receivable, Trade Payable, Inventory). No per-entry flagging here — that lives in Risk Assessment.",
      selectEng: "Select an engagement first.",
      correlation: "Correlation",
      subLedgerTab: "Sub GL Analyzer — Sales/Receivable",
      tradePayableTab: "Sub GL Analyzer — Trade Payable",
      inventoryTab: "Sub GL Analyzer — Inventory",
    },
    correlationTab: {
      title: "Control-account correlation",
      downloadBtn: "Download",
      downloadCsv: "Download as CSV",
      downloadXls: "Download as XLS",
      downloadXlsx: "Download as XLSX",
      recategoriseBtn: "Recategorise for Analyzer",
      recomputeBtn: "Recompute",
      runningBtn: "Running…",
      pairSelection: "Pair selection",
      resetDefaults: "Reset to defaults",
      addPair: "Add pair",
      categoryAPh: "Category A",
      categoryBPh: "Category B",
      removeAria: "Remove {a} vs {b}",
      whatVarianceAria: "What is Variance?",
      whatInterpretationAria: "What is interpretation?",
      noPairsSelected: "No pairs selected — Recompute will use the 11-pair default preset.",
      selectionDirtyPre: "Pair selection has changed — hit ",
      selectionDirtyBold: "Recompute",
      selectionDirtyPost: " to compute the new pairs. The results table below still shows the previous run.",
      emptyCache: "No correlation cached yet — hit Recompute to build the first snapshot from this engagement's Main GL.",
      computedLine: "Computed {when} · rows analysed: {rows} · unmapped accounts: {unmapped}",
      colPair: "Pair",
      colNPeriods: "n periods",
      colKendall: "Kendall τ-b",
      colPValue: "p-value",
      colPearson: "Pearson r",
      colReading: "Reading",
      variance: "Variance",
      interpretationDefault: "Interpretation",
      interpretationTooltipDefault: "Association, not causation. Prompt for enquiry — not a finding, diagnosis, or conclusion.",
      varianceTooltip: 'Variance — reports what actually changed on each side of the pair over the period. Baseline window = first quarter of available periods (or first-half when fewer than 6 periods are available); recent window = the last quarter (or second-half). Change = (recent_mean − baseline_mean) / |baseline_mean|. If the baseline is small relative to the series\' own typical monthly movement (under 10% of its engagement-wide mean absolute movement), the value is reported as "early period ≈ zero" rather than as a distorted percentage — the account was effectively dormant in the baseline window so a %-change against it would be misleading. Uses the same signed net-movement convention (credit − debit) as the rest of the Correlation tab. This column reports the change; it does not judge it.',
      earlyPeriodZero: "{label} early period ≈ zero",
      dashPlaceholder: "{label} —",
      monthlyAggregates: "Monthly aggregates",
      colPeriod: "Period",
      colCategory: "Category",
      colNetMovement: "Net movement",
      colEntries: "Entries",
      drillIn: "Drill in",
      toastLoadFail: "Failed to load correlation cache",
      toastRecomputeQueued: "Recompute queued — running on the worker fleet.",
      toastRecomputeComplete: "Correlation recompute complete.",
      toastRecomputeFailed: "Recompute failed",
      toastCategoriesRefreshed: "Categories refreshed — recompute to apply.",
      toastRecategoriseFail: "Recategorise failed",
      toastExportedFmt: "Correlation exported ({fmt})",
      toastExportFailed: "Export failed",
      toastExportFailedStatus: "Export failed ({status})",
      toastPickTwo: "Pick two categories first.",
      toastPairSidesDiffer: "Pair sides must differ.",
      toastPairAlreadySelected: "This pair is already selected.",
      toastCapPairs: "Cap is {n} pairs per compute.",
      toastPairsReset: "Pair selection reset to the 11-pair preset.",
      jobFailed: "Job failed",
      jobTimedOut: "Job timed out",
      enqueueFailed: "Enqueue failed — no job_id in response",
    },
    inventoryTab: {
      title: "Sub GL Analyzer — Inventory",
      downloadTemplate: "Download Template",
      downloadCsv: "Download as CSV",
      downloadXlsx: "Download as XLSX",
      uploadBtn: "Upload Inventory Movements",
      recompute: "Recompute",
      running: "Running…",
      empty: "No analytics cached yet — upload inventory movements and hit Recompute.",
      computedRows: "Computed {when} · rows: {n}",
      uploadedInfo: " · uploaded {when} (file: {file})",
      interpretation: "Interpretation",
      downloadReport: "Download Report",
      drillIn: "Drill in",
      noData: "No data for this check yet.",
      drillTitle: "Drill-in — {check} · {period}",
      close: "Close",
      loading: "Loading…",
      noRows: "No rows.",
      drillTruncated: "Showing the first {n} of {total} rows — the drill-in is capped at {cap}.",
      slowThresholdBadge: "threshold: no movement in last {n} days",
      turnoverLabel: "Inventory turnover days — trailing 90d",
      slowLabel: "Slow-moving % — monthly",
      negLabel: "Negative-stock frequency — monthly",
      cPeriodEnd: "Period end",
      cInvAvg: "Inventory (trailing avg)",
      cCogs90d: "COGS (90d)",
      cTurnoverDays: "Turnover (days)",
      cSlowSku: "Slow SKUs",
      cActiveSku: "Active SKUs",
      cSlowValue: "Slow value",
      cTotalValue: "Total value",
      cSlowPct: "Slow %",
      cPeriod: "Period",
      cNegSku: "Negative SKUs",
      cRate: "Rate",
      dcType: "Type",
      dcTxId: "Tx ID",
      dcDate: "Date",
      dcSku: "SKU",
      dcQty: "Qty",
      dcAmount: "Amount",
      tRecomputeQueued: "Inventory recompute queued on the worker fleet.",
      tRecomputed: "Inventory analytics recomputed.",
      tRecomputeFailed: "Recompute failed",
      tDownloadFailedStatus: "Download failed ({status})",
      tDownloadFailed: "Download failed",
      tLoadFailed: "Failed to load analytics",
      tDrilldownFailed: "Drilldown failed",
      tTemplateDownloaded: "Template downloaded ({fmt})",
      tReportDownloaded: "Report downloaded ({fmt})",
      tJobFailed: "Job failed",
      tJobTimedOut: "Job timed out",
      tNoJobId: "No job_id",
    },
    ratios: {
      title: "Financial Ratios",
      subtitle: "16 standard ratios calculated from the trial balance", subtitleSrc: "16 standard ratios calculated from the {source}",
      group: "Group",
      ratio: "Ratio",
      value: "Value",
      formula: "Formula",
      interpretation: "Interpretation",
      liquidity: "Liquidity",
      leverage: "Leverage",
      profitability: "Profitability",
      efficiency: "Efficiency",
      remarksComments: "Remarks / Comments",
      addRemark: "Add remark…",
      underlyingTotals: "Underlying totals",
    },
    aiReadout: {
      title: "AI Readout",
      button: "AI Readout",
      regenerate: "Regenerate",
      generating: "Generating…",
      ready: "Readout ready",
      failed: "Readout failed",
      empty: "No readout yet — press AI Readout for a plain-language summary of this analysis.",
      generatedLine: "Generated {when} by {who} · {n} figures checked against the analysis",
      outOfDate: "Out of date — analysis re-run since",
      unreliable: "Readout could not be produced reliably — please try again",
      suggestedStep: "Suggested step:",
      previous: "Previous ({n})",
    },
  },
  ar: {
    daPage: {
      title: "التدقيق الرقمي",
      desc: "تحليلات الترابط الشامل ودفاتر الأستاذ الفرعية (المبيعات/الذمم المدينة، الذمم الدائنة، المخزون). لا يوجد تعليم لكل قيد هنا — ذلك يوجد في تقييم المخاطر.",
      selectEng: "اختر مهمة أولاً.",
      correlation: "الترابط",
      subLedgerTab: "محلل الأستاذ الفرعي — المبيعات/الذمم المدينة",
      tradePayableTab: "محلل الأستاذ الفرعي — الذمم الدائنة",
      inventoryTab: "محلل الأستاذ الفرعي — المخزون",
    },
    correlationTab: {
      title: "الارتباط بين حسابات الرقابة",
      downloadBtn: "تنزيل",
      downloadCsv: "تنزيل CSV",
      downloadXls: "تنزيل XLS",
      downloadXlsx: "تنزيل XLSX",
      recategoriseBtn: "إعادة تصنيف للمحلل",
      recomputeBtn: "إعادة الاحتساب",
      runningBtn: "قيد التشغيل…",
      pairSelection: "اختيار الأزواج",
      resetDefaults: "إعادة إلى الافتراضي",
      addPair: "إضافة زوج",
      categoryAPh: "الفئة أ",
      categoryBPh: "الفئة ب",
      removeAria: "إزالة {a} مقابل {b}",
      whatVarianceAria: "ما هو التباين؟",
      whatInterpretationAria: "ما هو التفسير؟",
      noPairsSelected: "لم يتم اختيار أي أزواج — ستستخدم إعادة الاحتساب مجموعة الـ 11 زوجًا الافتراضية.",
      selectionDirtyPre: "تغيّر اختيار الأزواج — انقر على ",
      selectionDirtyBold: "إعادة الاحتساب",
      selectionDirtyPost: " لاحتساب الأزواج الجديدة. لا يزال جدول النتائج أدناه يعرض التشغيل السابق.",
      emptyCache: "لا توجد ذاكرة تخزين مؤقت للارتباط بعد — انقر على إعادة الاحتساب لبناء اللقطة الأولى من الأستاذ العام الرئيسي لهذه المهمة.",
      computedLine: "احتُسب {when} · صفوف مُحللة: {rows} · حسابات غير مصنّفة: {unmapped}",
      colPair: "الزوج",
      colNPeriods: "عدد الفترات",
      colKendall: "كيندال τ-b",
      colPValue: "قيمة p",
      colPearson: "بيرسون r",
      colReading: "القراءة",
      variance: "التباين",
      interpretationDefault: "التفسير",
      interpretationTooltipDefault: "ارتباط، وليس سببية. مطالبة بالاستفسار — وليس استنتاجًا أو تشخيصًا نهائيًا.",
      varianceTooltip: 'التباين — يفيد بما تغيّر فعليًا على كل جانب من الزوج خلال الفترة. نافذة الأساس = الربع الأول من الفترات المتاحة (أو النصف الأول عندما تقل الفترات عن 6)؛ النافذة الحديثة = الربع الأخير (أو النصف الثاني). التغير = (متوسط الحديث − متوسط الأساس) / |متوسط الأساس|. إذا كان الأساس صغيرًا نسبيًا مقارنة بحركة السلسلة الشهرية النموذجية (أقل من 10% من متوسط الحركة المطلقة على مستوى المهمة)، يُبلَّغ عن القيمة بـ "الفترة المبكرة ≈ صفر" بدلاً من نسبة مشوّهة — كان الحساب خاملاً فعليًا في نافذة الأساس، وبالتالي فإن نسبة التغير عليه ستكون مضللة. يستخدم نفس اصطلاح صافي الحركة المُشار (دائن − مدين) المستخدم في بقية تبويب الارتباط. يُفيد هذا العمود بالتغير؛ ولا يحكم عليه.',
      earlyPeriodZero: "{label} الفترة المبكرة ≈ صفر",
      dashPlaceholder: "{label} —",
      monthlyAggregates: "المجاميع الشهرية",
      colPeriod: "الفترة",
      colCategory: "الفئة",
      colNetMovement: "صافي الحركة",
      colEntries: "الإدخالات",
      drillIn: "تعمّق",
      toastLoadFail: "فشل تحميل ذاكرة التخزين المؤقت للارتباط",
      toastRecomputeQueued: "تم إدراج إعادة الاحتساب في قائمة الانتظار — قيد التنفيذ على أسطول العمال.",
      toastRecomputeComplete: "اكتملت إعادة احتساب الارتباط.",
      toastRecomputeFailed: "فشلت إعادة الاحتساب",
      toastCategoriesRefreshed: "تم تحديث التصنيفات — أعد الاحتساب لتطبيقها.",
      toastRecategoriseFail: "فشلت إعادة التصنيف",
      toastExportedFmt: "تم تصدير الارتباط ({fmt})",
      toastExportFailed: "فشل التصدير",
      toastExportFailedStatus: "فشل التصدير ({status})",
      toastPickTwo: "اختر فئتين أولاً.",
      toastPairSidesDiffer: "يجب أن تختلف جهتا الزوج.",
      toastPairAlreadySelected: "هذا الزوج مُختار مسبقًا.",
      toastCapPairs: "الحد الأقصى {n} زوجًا لكل احتساب.",
      toastPairsReset: "تمت إعادة اختيار الأزواج إلى مجموعة الـ 11 زوجًا الافتراضية.",
      jobFailed: "فشلت المهمة",
      jobTimedOut: "انتهت مهلة المهمة",
      enqueueFailed: "فشل الإدراج في قائمة الانتظار — لا يوجد job_id في الاستجابة",
    },
    inventoryTab: {
      title: "محلل الأستاذ الفرعي — المخزون",
      downloadTemplate: "تحميل القالب",
      downloadCsv: "تحميل كـ CSV",
      downloadXlsx: "تحميل كـ XLSX",
      uploadBtn: "رفع حركات المخزون",
      recompute: "إعادة حساب",
      running: "جاري التشغيل…",
      empty: "لا توجد تحليلات مخزّنة بعد — قم برفع حركات المخزون واضغط \"إعادة حساب\".",
      computedRows: "احتُسبت {when} · صفوف: {n}",
      uploadedInfo: " · تم الرفع {when} (ملف: {file})",
      interpretation: "التفسير",
      downloadReport: "تحميل التقرير",
      drillIn: "تعمّق",
      noData: "لا توجد بيانات لهذا الفحص بعد.",
      drillTitle: "تعمّق — {check} · {period}",
      close: "إغلاق",
      loading: "جاري التحميل…",
      noRows: "لا توجد صفوف.",
      drillTruncated: "عرض أول {n} من أصل {total} صف — تعمّق التنقيب محدود بـ {cap}.",
      slowThresholdBadge: "الحد: لا حركة في آخر {n} يوماً",
      turnoverLabel: "أيام دوران المخزون — آخر 90 يوماً",
      slowLabel: "% البطيء — شهرياً",
      negLabel: "تكرار المخزون السالب — شهرياً",
      cPeriodEnd: "نهاية الفترة",
      cInvAvg: "المخزون (متوسط متتابع)",
      cCogs90d: "التكلفة (90 يوماً)",
      cTurnoverDays: "الدوران (أيام)",
      cSlowSku: "SKUs بطيئة",
      cActiveSku: "SKUs نشطة",
      cSlowValue: "قيمة البطيء",
      cTotalValue: "القيمة الإجمالية",
      cSlowPct: "% البطيء",
      cPeriod: "الفترة",
      cNegSku: "SKUs سالبة",
      cRate: "المعدل",
      dcType: "النوع",
      dcTxId: "رقم المعاملة",
      dcDate: "التاريخ",
      dcSku: "SKU",
      dcQty: "الكمية",
      dcAmount: "المبلغ",
      tRecomputeQueued: "تم إدراج إعادة حساب المخزون في قائمة الانتظار على أسطول العمّال.",
      tRecomputed: "تمت إعادة حساب تحليلات المخزون.",
      tRecomputeFailed: "فشل إعادة الحساب",
      tDownloadFailedStatus: "فشل التحميل ({status})",
      tDownloadFailed: "فشل التحميل",
      tLoadFailed: "فشل تحميل التحليلات",
      tDrilldownFailed: "فشل التعمّق",
      tTemplateDownloaded: "تم تحميل القالب ({fmt})",
      tReportDownloaded: "تم تحميل التقرير ({fmt})",
      tJobFailed: "فشلت المهمة",
      tJobTimedOut: "انتهت مهلة المهمة",
      tNoJobId: "لا يوجد معرف مهمة",
    },
    ratios: {
      title: "النسب المالية",
      subtitle: "16 نسبة قياسية محسوبة من ميزان المراجعة", subtitleSrc: "16 نسبة قياسية محسوبة من {source}",
      group: "المجموعة",
      ratio: "النسبة",
      value: "القيمة",
      formula: "الصيغة",
      interpretation: "التفسير",
      liquidity: "السيولة",
      leverage: "الرافعة المالية",
      profitability: "الربحية",
      efficiency: "الكفاءة",
      remarksComments: "الملاحظات / التعليقات",
      addRemark: "أضف ملاحظة…",
      underlyingTotals: "الإجماليات الأساسية",
    },
    aiReadout: {
      title: "القراءة الذكية",
      button: "القراءة الذكية",
      regenerate: "إعادة التوليد",
      generating: "جارٍ التوليد…",
      ready: "القراءة جاهزة",
      failed: "تعذّر إنتاج القراءة",
      empty: "لا توجد قراءة بعد — اضغط \"القراءة الذكية\" للحصول على ملخص بلغة بسيطة لهذا التحليل.",
      generatedLine: "أُنشئت {when} بواسطة {who} · تم التحقق من {n} رقمًا مقابل التحليل",
      outOfDate: "قديمة — أُعيد تشغيل التحليل بعدها",
      unreliable: "تعذّر إنتاج القراءة بشكل موثوق — يرجى المحاولة مرة أخرى",
      suggestedStep: "الخطوة المقترحة:",
      previous: "السابقة ({n})",
    },
  },
};

export default I18N_BASE_DIGITAL_AUDIT_TABS;
